// Route table shared by useRoutes (router/index.ts), the prerender (entry-server.tsx) and
// preloadRoute (main.tsx). Pages are lazyPage() so a prerendered route's chunk can be loaded
// before hydrateRoot (see _seo-kit/pattern/PATTERN.md, "Preload the route chunk").
import type { ComponentType } from 'react';
import { matchRoutes, type RouteObject } from 'react-router-dom';
import { lazyPage } from '../lib/lazyPage';

const HomePage = lazyPage(() => import('../pages/home/page'));
const NotFound = lazyPage(() => import('../pages/NotFound'));
const HospitalLeadersPage = lazyPage(() => import('../pages/hospital-leaders/page'));
const CliniciansPage = lazyPage(() => import('../pages/clinicians/page'));
const PatientsPage = lazyPage(() => import('../pages/patients/page'));
const HospitalistMedicinePage = lazyPage(() => import('../pages/services/hospitalist-medicine/page'));
const ObservationUnitsPage = lazyPage(() => import('../pages/services/observation-units/page'));
const NursingHomeServicesPage = lazyPage(() => import('../pages/services/nursing-home-services/page'));
const IMConsultationsPage = lazyPage(() => import('../pages/services/im-consultations/page'));
const SurgicalSpecialtySupportPage = lazyPage(() => import('../pages/services/surgical-specialty-support/page'));
const TransitionalCareProgramPage = lazyPage(() => import('../pages/services/transitional-care-program/page'));

type Page = { path: string; Component: ComponentType & { preload?: () => Promise<unknown> } };

/** The catch-all "*" (404) must stay last. */
const pages: Page[] = [
  { path: '/', Component: HomePage },
  { path: '/hospital-leaders', Component: HospitalLeadersPage },
  { path: '/clinicians', Component: CliniciansPage },
  { path: '/patients', Component: PatientsPage },
  { path: '/services/hospitalist-medicine', Component: HospitalistMedicinePage },
  { path: '/services/observation-units', Component: ObservationUnitsPage },
  { path: '/services/nursing-home-services', Component: NursingHomeServicesPage },
  { path: '/services/im-consultations', Component: IMConsultationsPage },
  { path: '/services/surgical-specialty-support', Component: SurgicalSpecialtySupportPage },
  { path: '/services/transitional-care-program', Component: TransitionalCareProgramPage },
  { path: '*', Component: NotFound },
];

const routes: RouteObject[] = pages.map(({ path, Component }) => ({ path, element: <Component /> }));

/** Loads the lazy page chunk matching `pathname`; main.tsx awaits it before hydrateRoot. */
export function preloadRoute(pathname: string): Promise<unknown> {
  const matches = matchRoutes(pages, pathname, __BASE_PATH__) ?? [];
  return Promise.all(matches.map((m) => (m.route as Page).Component.preload?.()));
}

export default routes;
